/**
 * KeepAliveOutlet — drop-in replacement for react-router-dom's <Outlet />.
 *
 * Renders every visited route into a hidden DOM subtree (display: none) instead
 * of unmounting it on navigation. Local component state, scroll position,
 * focused element, and in-flight requests survive when the user navigates away
 * and comes back.
 *
 * Bounded by an LRU cap. Most-recently-visited path is always kept; oldest is
 * evicted first when the cap is exceeded.
 */
import React, { useRef } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { Box } from '@mui/material';

const MAX_CACHE = 12;

// Paths whose state we don't want to preserve. /login is outside AppLayout so
// it never reaches here; excluded defensively.
// Dashboards are excluded: they poll live data and RGL/DashboardProvider
// misbehave as hidden cached copies (duplicate mounts + dead drag events —
// DEC-063 P2 pilot finding). Fresh mount per visit is correct for them.
const EXCLUDED_PATTERNS: RegExp[] = [/^\/login$/, /\/dashboard$/];

function isExcluded(path: string, hasOutlet: boolean): boolean {
  if (!hasOutlet) return true;
  return EXCLUDED_PATTERNS.some((re) => re.test(path));
}

export const KeepAliveOutlet: React.FC = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const path = location.pathname;
  const cacheRef = useRef<Map<string, React.ReactNode>>(new Map());

  if (isExcluded(path, !!outlet)) {
    return <>{outlet}</>;
  }

  // Refs aren't reactive — mutating during render is the React-blessed way to
  // keep derived state in sync with props/hooks without an extra render cycle.
  const cache = cacheRef.current;
  if (!cache.has(path)) {
    cache.set(path, outlet);
  } else {
    // Path already cached — keep the original element reference so the React
    // tree underneath stays identity-stable, and bump it to the MRU slot.
    const existing = cache.get(path)!;
    cache.delete(path);
    cache.set(path, existing);
  }
  while (cache.size > MAX_CACHE) {
    const firstKey = cache.keys().next().value;
    if (firstKey === undefined || firstKey === path) break;
    cache.delete(firstKey);
  }

  return (
    <>
      {Array.from(cache.entries()).map(([cachedPath, node]) => (
        <Box
          key={cachedPath}
          sx={{ display: cachedPath === path ? 'block' : 'none' }}
          aria-hidden={cachedPath !== path}
        >
          {node}
        </Box>
      ))}
    </>
  );
};

export default KeepAliveOutlet;
